<script setup lang="ts">
import BirthDateInputCard from "@/views/Calculators/DestinyMatrix/components/BirthDateInputCard.vue";
import { computed, onBeforeMount, ref } from "vue";
import { DestinyMatrix } from "@/types/destinyMatrix";
import { downloadPdfResults, fetchDestinyMatrix } from "@/api/destinyMatrix";
import DestinyMatrixComponent from "@/views/Calculators/DestinyMatrix/components/DestinyMatrixComponent.vue";
import HealthMapComponent from "@/views/Calculators/DestinyMatrix/components/HealthMapComponent.vue";
import PersonalCardComponent from "@/views/Calculators/DestinyMatrix/components/PersonalCardComponent.vue";
import MatrixDescriptionComponent from "@/views/Calculators/DestinyMatrix/components/MatrixDescriptionComponent.vue";
import { useUserStore } from "@/stores/userStore";
import { PREMIUM_CALCULATORS } from "@/types/premiumTypes";
import { datePartsToString, normalizeStringDate } from "@/utils/date";
import { useRoute } from "vue-router";
import GradientButton from "@/components/buttons/GradientButton.vue";
import { useI18n } from "vue-i18n";

const authStore = useUserStore();
const route = useRoute();
const { t } = useI18n();

const waitApiCall = ref(false);
const destinyMatrix = ref<DestinyMatrix>();
const initDate = ref<Date>();
const userName = ref<string | null>(null);
const submittedDate = ref("");
const userHasSubscription = ref(false);
const userHasAssessToResults = computed(() => {
  if (userHasSubscription.value) return true;

  return isUserPurchasedDate(submittedDate.value);
});

onBeforeMount(async () => {
  await initTodayMatrix();
});

async function getMatrixData(day: number, month: number, year: number) {
  const newRequestedDate = datePartsToString(day, month, year);
  try {
    waitApiCall.value = true;
    destinyMatrix.value = await fetchDestinyMatrix(day, month, year);
    submittedDate.value = newRequestedDate;
  } catch (error) {
    console.log(error);
  } finally {
    waitApiCall.value = false;
  }
}

async function initTodayMatrix() {
  initDate.value = getInitDate();
  await authStore.updateUserInfo();
  userHasSubscription.value = authStore.userHasSubscription(
    PREMIUM_CALCULATORS.destinyMatrix
  );

  const requestedDate = datePartsToString(
    initDate.value.getDate(),
    initDate.value.getMonth() + 1,
    initDate.value.getFullYear()
  );

  destinyMatrix.value = await fetchDestinyMatrix(
    initDate.value.getDate(),
    initDate.value.getMonth() + 1,
    initDate.value.getFullYear()
  );
  submittedDate.value = requestedDate;
}

function getInitDate() {
  const dateString = route.query.init_date;
  if (!dateString) {
    return new Date();
  }
  const initDate = normalizeStringDate(dateString as string);

  return initDate ?? new Date();
}

function isUserPurchasedDate(requestedDate: string): boolean {
  if (authStore.user === null) return false;

  const products = authStore.user.products;

  return products["destiny_matrix"]?.includes(requestedDate) ?? false;
}

async function downloadPDF() {
  if (submittedDate.value === "") {
    return;
  }
  try {
    waitApiCall.value = true;
    const apiDate = submittedDate.value.split("-").reverse().join("-");
    const blob = await downloadPdfResults(apiDate, userName.value);
    const downloadUrl = window.URL.createObjectURL(
      new Blob([blob], { type: "application/pdf" })
    );
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.setAttribute("download", `Matrix_${apiDate}.pdf`);
    document.body.appendChild(link);
    link.click();
    if (link.parentNode) {
      link.parentNode.removeChild(link);
    }
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.log(error);
  } finally {
    waitApiCall.value = false;
  }
}
</script>

<template>
  <div class="overflow-y-auto no-scrollbar h-full w-full">
    <div
      class="max-w-6xl mx-auto pt-2 xl:pt-[81px] md:pb-4 px-1 flex flex-col md:flex-row gap-4"
    >
      <div class="flex flex-col w-full md:w-[53%] gap-4 inline-size">
        <BirthDateInputCard
          class="date-input"
          :isLoading="waitApiCall"
          :initDate="initDate"
          @calculateMatrix="getMatrixData"
          v-model="userName"
        />
        <DestinyMatrixComponent
          class="schema aspect-square shrink-0"
          :matrix="destinyMatrix"
        />
      </div>

      <div class="flex flex-col w-full md:w-[47%] gap-4">
        <HealthMapComponent class="health-map" :matrix="destinyMatrix" />
        <PersonalCardComponent class="personal-card" :matrix="destinyMatrix" />
      </div>
    </div>
    <div class="max-w-6xl mx-auto px-1 mt-4">
      <MatrixDescriptionComponent
        class="accordion"
        :matrix="destinyMatrix"
        :submittedDate="submittedDate"
        :userHasPremiumAccess="userHasAssessToResults"
      />
    </div>

    <div
      v-if="userHasSubscription || userHasAssessToResults"
      class="max-w-6xl mx-auto px-1 my-4"
    >
      <GradientButton
        class="rounded-xl shrink-0 h-10"
        bg-color="bg-brown-gradient"
        hover-bg-color="bg-orange-gradient"
        :text="t('buttons.download-as-pdf')"
        @click="downloadPDF"
      >
        <template #icon><div></div></template>
      </GradientButton>
    </div>
  </div>
</template>

<style scoped>
.health-map {
  grid-area: health-map;
}
.schema {
  grid-area: schema;
}
.personal-card {
  grid-area: personal-card;
}
.accordion {
  grid-area: accordion;
}

.inline-size {
  contain: inline-size;
}
</style>
